import React, { useEffect, useState } from 'react';
import { Table, Input, Icon, Dropdown, Button } from 'semantic-ui-react';
import moment from 'moment';
import Heading from '../../components/Heading';
import Loader from '../../components/Loader';
import ErrorConsoleAI from '../../components/ErrorConsoleAI';
import fetchCronJobs from '../../services/fetchCronJobs';
import user from '../../store/user';
import './CronJobs.scss';

const CronJobs = () => {
    const [cronJobs, setCronJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [limit, setLimit] = useState(50);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isConsoleOpen, setIsConsoleOpen] = useState(false);
    const [adminId, setAdminId] = useState(null);

    const limitOptions = [
        { key: 10, text: '10', value: 10 },
        { key: 25, text: '25', value: 25 },
        { key: 50, text: '50', value: 50 },
        { key: 100, text: '100', value: 100 },
        { key: 'all', text: 'All', value: 999999 }
    ];

    useEffect(() => {
        loadCronJobs();
    }, [limit]);

    const loadCronJobs = async () => {
        try {
            setIsLoading(true);
            const profile = await user.getProfile();
            setAdminId(profile.id);
            const response = await fetchCronJobs(profile.id, { limit });
            
            if (response.data && response.data.data && response.data.data.jobs) {
                setCronJobs(response.data.data.jobs);
                setFilteredJobs(response.data.data.jobs);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching cron jobs:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredJobs(cronJobs);
        } else {
            const filtered = cronJobs.filter(job =>
                job.jobName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredJobs(filtered);
        }
    }, [searchTerm, cronJobs]);

    const getStatusClass = (status) => {
        if (!status) return '';
        return status.toLowerCase() === 'success' ? 'status-success' : 'status-failure';
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
        return `${(ms / 60000).toFixed(2)}m`;
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return moment(date).format('DD MMM YYYY, HH:mm:ss');
    };

    const formatRelativeTime = (date) => {
        if (!date) return '';
        return moment(date).fromNow();
    };

    const handleOpenErrorConsole = (job) => {
        setSelectedJob({ ...job, adminId });
        setIsConsoleOpen(true);
    };

    const handleCloseErrorConsole = () => {
        setIsConsoleOpen(false);
        setSelectedJob(null);
    };

    return (
        <div className="cron-jobs-container">
            <div className="page-header">
                <Heading text="Cron Jobs Monitor" />
            </div>

            <div className="controls-section">
                <div className="search-box">
                    <Input
                        icon="search"
                        placeholder="Search by job name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="limit-selector">
                    <span className="limit-label">Show:</span>
                    <Dropdown
                        selection
                        options={limitOptions}
                        value={limit}
                        onChange={(e, { value }) => setLimit(value)}
                        className="limit-dropdown"
                    />
                </div>
                <div className="refresh-button">
                    <Icon 
                        name="refresh" 
                        size="large" 
                        link 
                        onClick={loadCronJobs}
                        title="Refresh"
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="loader-wrapper">
                    <Loader />
                </div>
            ) : (
                <>
                    <div className="stats-summary">
                        <div className="stat-card">
                            <div className="stat-value">{filteredJobs.length}</div>
                            <div className="stat-label">Total Jobs</div>
                        </div>
                        <div className="stat-card success">
                            <div className="stat-value">
                                {filteredJobs.filter(j => j.state && j.state.currentStatus === 'success').length}
                            </div>
                            <div className="stat-label">Successful</div>
                        </div>
                        <div className="stat-card failure">
                            <div className="stat-value">
                                {filteredJobs.filter(j => j.state && j.state.currentStatus !== 'success').length}
                            </div>
                            <div className="stat-label">Failed</div>
                        </div>
                    </div>

                    <div className="table-wrapper">
                        <Table celled striped className="cron-jobs-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Job Name</Table.HeaderCell>
                                    <Table.HeaderCell>Status</Table.HeaderCell>
                                    <Table.HeaderCell>Last Run</Table.HeaderCell>
                                    <Table.HeaderCell>Duration</Table.HeaderCell>
                                    <Table.HeaderCell>Total Runs</Table.HeaderCell>
                                    <Table.HeaderCell>Success Rate</Table.HeaderCell>
                                    <Table.HeaderCell>Consecutive Failures</Table.HeaderCell>
                                    <Table.HeaderCell>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {filteredJobs.length === 0 ? (
                                    <Table.Row>
                                        <Table.Cell colSpan="8" textAlign="center">
                                            No cron jobs found
                                        </Table.Cell>
                                    </Table.Row>
                                ) : (
                                    filteredJobs.map((job, index) => (
                                        <Table.Row 
                                            key={index}
                                            className={getStatusClass(job.state && job.state.currentStatus)}
                                        >
                                            <Table.Cell className="job-name-cell">
                                                <div className="job-name">{job.jobName}</div>
                                            </Table.Cell>
                                            <Table.Cell className="status-cell">
                                                <div className={`status-badge ${getStatusClass(job.state && job.state.currentStatus)}`}>
                                                    <Icon 
                                                        name={job.state && job.state.currentStatus === 'success' ? 'check circle' : 'times circle'} 
                                                    />
                                                    {job.state && job.state.currentStatus || 'Unknown'}
                                                </div>
                                                {job.state && job.state.currentStatus !== 'success' && job.state.error && (
                                                    <div className="error-message" title={job.state.error}>
                                                        {job.state.error}
                                                    </div>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="last-run-time">
                                                    {formatDate(job.state && job.state.lastRunAt)}
                                                </div>
                                                <div className="relative-time">
                                                    {formatRelativeTime(job.state && job.state.lastRunAt)}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {formatDuration(job.state && job.state.lastDurationMs)}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {job.metrics && job.metrics.totalRuns || 0}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="success-rate">
                                                    {job.metrics && job.metrics.successRate ? `${job.metrics.successRate}%` : 'N/A'}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className={`consecutive-failures ${job.metrics && job.metrics.consecutiveFailures > 0 ? 'has-failures' : ''}`}>
                                                    {job.metrics && job.metrics.consecutiveFailures || 0}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="actions-cell">
                                                {job.state && job.state.currentStatus !== 'success' && (
                                                    <Button
                                                        size="small"
                                                        color="red"
                                                        icon
                                                        labelPosition="left"
                                                        onClick={() => handleOpenErrorConsole(job)}
                                                        className="error-console-btn"
                                                    >
                                                        <Icon name="bug" />
                                                        Debug
                                                    </Button>
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))
                                )}
                            </Table.Body>
                        </Table>
                    </div>
                </>
            )}

            {/* Error Console AI */}
            {selectedJob && (
                <ErrorConsoleAI
                    isOpen={isConsoleOpen}
                    onClose={handleCloseErrorConsole}
                    jobData={selectedJob}
                />
            )}
        </div>
    );
};

export default CronJobs;
